import { NO_DATA_SIGN } from "app/filters/const";
import { FORMATTERS } from "app/common/benchmark/formatters";

import { getKeywordsData, getProgressBarValue, sortByDomainOrder } from "../../helpers/helpers";
import type {
    SearchDataSchema,
    SearchKeywordsDataSchema,
    SearchTrafficDataSchema,
} from "../../services/search-overview-api/types";
import type { SearchKeywordsData, ESourceType } from "../../types";

import type { OrganicPaidTableAction, TransformedDataEntry } from "./types";

const buildMetricData = (data?: SearchDataSchema) => ({
    visits: data?.Average ? FORMATTERS.numberAbbr(data.Average) : NO_DATA_SIGN,
    change: data?.YoyChange,
});
interface TransformedOrganicPaidData {
    domain: string;
    organic: { visits: string; change?: number };
    paid: { visits: string; change?: number };
    percentageOf: {
        organic: {
            formattedValue: string;
            numericValue: number;
        };
        paid: {
            formattedValue: string;
            numericValue: number;
        };
    };
}

export const transformOrganicPaidTrafficData = (
    trafficData: SearchTrafficDataSchema,
    dispatch: React.Dispatch<OrganicPaidTableAction>,
) => {
    if (!trafficData?.TrafficSourcesVisits) {
        return;
    }

    const transformedArray = Object.entries(trafficData.TrafficSourcesVisits).reduce((acc, [domain, domainMetrics]) => {
        if (!domainMetrics) {
            return acc;
        }

        acc.push({
            domain,
            organic: buildMetricData(domainMetrics?.Organic),
            paid: buildMetricData(domainMetrics?.Paid),
            percentageOf: {
                organic: getProgressBarValue(domainMetrics?.Organic?.Total, domainMetrics?.Organic?.TotalVisits),
                paid: getProgressBarValue(domainMetrics?.Paid?.Total, domainMetrics?.Paid?.TotalVisits),
            },
        });

        return acc;
    }, [] as TransformedOrganicPaidData[]);

    const sortedArray = sortByDomainOrder(transformedArray, (item) => item.domain);

    const sortedObject: TransformedDataEntry = sortedArray.reduce((acc, item) => {
        const { domain, ...rest } = item;
        acc[domain] = rest;
        return acc;
    }, {} as TransformedDataEntry);

    dispatch({ type: "SET_ORGANIC_PAID_DATA", payload: sortedObject });
};

export const transformOrganicPaidKeywordsData = (
    data: SearchKeywordsDataSchema,
    sourceType: ESourceType.ORGANIC | ESourceType.PAID,
    dispatch: React.Dispatch<OrganicPaidTableAction>,
) => {
    if (!data) {
        return;
    }

    const transformedPayload = Object.entries(data).reduce(
        (acc, [domain, value]) => {
            if (!value) {
                return acc;
            }
            acc.push({ domain, data: getKeywordsData(value) });
            return acc;
        },
        [] as { domain: string; data: SearchKeywordsData }[],
    );

    const sortedArray = sortByDomainOrder(transformedPayload, (item) => item.domain);

    const sortedObject = sortedArray.reduce(
        (acc, item) => {
            acc[item.domain] = item.data;
            return acc;
        },
        {} as Record<string, SearchKeywordsData>,
    );
    dispatch({ type: "SET_KEYWORDS_DATA", payload: sortedObject, sourceType });
};
